import { Browser } from '@capacitor/browser'
import { App, type URLOpenListenerEvent } from '@capacitor/app'
import type { PluginListenerHandle } from '@capacitor/core'

import { apiBaseUrl, isNativePlatform } from './platform'
import { setTokens } from './tokenStore'

// Must match the BFF's MOBILE_OAUTH_REDIRECT_URL and the custom URL scheme
// registered in the iOS/Android projects.
const OAUTH_CALLBACK_PREFIX = 'crapapp://oauth-callback'

export type OAuthOutcome = { ok: true } | { ok: false; error: string }

// Opens the system browser (SFSafariViewController / Chrome Custom Tabs) to the
// BFF OAuth login endpoint, flagged as a mobile flow. The BFF redirects tokens
// back through the crapapp:// deep link, which registerOAuthDeepLinkListener
// captures. Embedded WKWebViews are disallowed by Google for OAuth, so we must
// use the system browser here.
export async function startNativeOAuth(provider: 'google' | 'facebook'): Promise<void> {
    const url = `${apiBaseUrl()}/api/oauth/${provider}/login?mobile=1`
    await Browser.open({ url })
}

// Registers a deep-link listener that completes the native OAuth flow: parses
// tokens (or an error) from the crapapp://oauth-callback fragment, persists the
// tokens, and closes the in-app browser. Returns a handle so callers can remove
// the listener on unmount. No-op (null) on web.
export async function registerOAuthDeepLinkListener(
    onComplete: (outcome: OAuthOutcome) => void,
): Promise<PluginListenerHandle | null> {
    if (!isNativePlatform()) return null

    return App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
        const raw = event.url ?? ''
        if (!raw.startsWith(OAUTH_CALLBACK_PREFIX)) return

        const fragment = raw.includes('#') ? raw.slice(raw.indexOf('#') + 1) : ''
        const params = new URLSearchParams(fragment)
        const error = params.get('error')
        const accessToken = params.get('access_token')
        const refreshToken = params.get('refresh_token')

        // The provider flow is done — dismiss the in-app browser either way.
        try {
            await Browser.close()
        } catch {
            // Browser may already be closed on some platforms.
        }

        if (error || !accessToken) {
            onComplete({ ok: false, error: error ?? 'oauth_failed' })
            return
        }
        await setTokens(accessToken, refreshToken ?? undefined)
        onComplete({ ok: true })
    })
}
